// src/components/TransportMarkerListOrder.js

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
/* global axios */
import api from '../services/apiService';
import { Helmet } from 'react-helmet';  // Import von React Helmet

const TransportMarkerListOrder = () => {
  const { orderId } = useParams(); // Hole die orderId aus den URL-Parametern
  const navigate = useNavigate();

  // Zustände für TransportMarkers, OrderItems und den Auftrag
  const [transportMarkers, setTransportMarkers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newMarker, setNewMarker] = useState({
    orderItemId: '',
    delivery: '',
    pickup: '',
    operationLocationDelivery: '',
    operationLocationPickup: '',
    transportNotes: '',
  });
  const [orderItems, setOrderItems] = useState([]);
  const [order, setOrder] = useState(null); // Zustand für den Auftrag

  // Zustände für Auto-Erstellung
  const [autoCreated, setAutoCreated] = useState(false);
  const [isAutoCreating, setIsAutoCreating] = useState(false);

  // Zusätzliche Zustände für Bearbeitung
  const [editMarkerId, setEditMarkerId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    orderItemId: '',
    delivery: '',
    pickup: '',
    operationLocationDelivery: '',
    operationLocationPickup: '',
    transportNotes: '',
  });

  // Lade Order-Daten, OrderItems und TransportMarkers in Reihenfolge
  useEffect(() => {
    const fetchData = async () => {
      await fetchOrder();         // Erst Order-Daten laden
      await fetchOrderItems();    // Dann OrderItems laden
      await fetchTransportMarkers(); // Dann TransportMarkers laden
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Funktion zum Abrufen der Order-Daten
  const fetchOrder = async () => {
    try {
      const response = await api.get(`/orders/${orderId}/details`);
      setOrder(response.data);
      console.log(`Order-Daten für Auftrag ${orderId}:`, response.data);
    } catch (error) {
      console.error('Fehler beim Abrufen des Auftrags:', error);
      alert('Fehler beim Laden der Auftragsdaten.');
    }
  };

  // Funktion zum Abrufen aller OrderItems für den Auftrag
  const fetchOrderItems = async () => {
    try {
      const response = await api.get(`/orders/${orderId}/orderitems`);
      const filteredItems = response.data.filter(
        (item) => item.item && item.item.itemType === 'rental' && item.item.visibleInCalendar
      );
      setOrderItems(filteredItems);
    } catch (error) {
      console.error('Fehler beim Abrufen der OrderItems:', error);
      alert('Fehler beim Laden der OrderItems.');
    }
  };

  // Funktion zum Abrufen aller TransportMarkers für den Auftrag
  const fetchTransportMarkers = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/transportmarkers/byOrder/${orderId}?t=${new Date().getTime()}`);
      setTransportMarkers(response.data);
      console.log(`TransportMarkers für Auftrag ${orderId}:`, response.data);

      // Prüfen, ob bereits TransportMarkers automatisch erstellt wurden
      const rentalOrderItems = orderItems.filter(item => item.item && item.item.itemType === 'rental');
      const autoCreatedMarkers = response.data.filter(marker =>
        rentalOrderItems.some(item => item.id === marker.orderItemId)
      );

      if (autoCreatedMarkers.length > 0) {
        setAutoCreated(true);
      }
    } catch (error) {
      console.error('Fehler beim Abrufen der TransportMarkers:', error);
      if (error.response && error.response.status === 404) {
        alert('Keine TransportMarkers für diesen Auftrag gefunden.');
      } else {
        alert('Fehler beim Laden der TransportMarkers.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Funktion zum Abrufen eines einzelnen TransportMarkers
  const fetchTransportMarkerById = async (id) => {
    try {
      const response = await api.get(`/transportmarkers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Fehler beim Abrufen des TransportMarkers:', error);
      alert('Fehler beim Laden des TransportMarkers.');
      return null;
    }
  };

  // Handler zum Öffnen des Bearbeitungsformulars
  const handleEditMarker = async (markerId) => {
    const markerData = await fetchTransportMarkerById(markerId);
    if (markerData) {
      setEditMarkerId(markerId);
      setEditFormData({
        orderItemId: markerData.orderItemId || '',
        delivery: markerData.delivery ? markerData.delivery.slice(0, 10) : '', // Nur Datum (YYYY-MM-DD)
        pickup: markerData.pickup ? markerData.pickup.slice(0, 10) : '', // Nur Datum (YYYY-MM-DD)
        operationLocationDelivery: markerData.operationLocationDelivery || '',
      operationLocationPickup: markerData.operationLocationPickup || '',
      transportNotes: markerData.transportNotes || '',
      });
      setShowForm(true);
    }
  };

  // Handler für Änderungen im Bearbeitungsformular
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handler für Änderungen im Erstellungsformular
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewMarker((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handler zum Absenden des Bearbeitungsformulars
  const handleEditFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!editFormData.orderItemId) {
        alert('Bitte ein OrderItem auswählen.');
        return;
      }

      const payload = {
        orderItemId: parseInt(editFormData.orderItemId, 10),
        lwOperatorId: order?.lwOperator?.id ?? null, // Setzt den LW Operator aus dem Auftrag
        delivery: editFormData.delivery ? new Date(editFormData.delivery).toISOString() : null,
        pickup: editFormData.pickup ? new Date(editFormData.pickup).toISOString() : null,
        operationLocationDelivery: editFormData.operationLocationDelivery,
        operationLocationPickup: editFormData.operationLocationPickup,
        transportNotes: editFormData.transportNotes,
      };

      const response = await api.put(`/transportmarkers/${editMarkerId}`, payload);
      console.log('TransportMarker aktualisiert:', response.data);
      alert('TransportMarker erfolgreich aktualisiert!');
      setShowForm(false);
      setEditMarkerId(null);
      fetchTransportMarkers(); // Aktualisiere die Liste
    } catch (error) {
      console.error('Fehler beim Aktualisieren des TransportMarkers:', error);
      if (error.response && error.response.data && error.response.data.message) {
        alert(`Fehler: ${error.response.data.message}`);
      } else {
        alert('Fehler beim Aktualisieren des TransportMarkers.');
      }
    }
  };

  // Handler zum Erstellen eines neuen TransportMarkers
  const handleCreateMarker = async (e) => {
    e.preventDefault();
    try {
      if (!newMarker.orderItemId) {
        alert('Bitte ein OrderItem auswählen.');
        return;
      }

      const payload = {
        orderItemId: parseInt(newMarker.orderItemId, 10),
        lwOperatorId: order?.lwOperator?.id ?? null, // Setzt den LW Operator aus dem Auftrag
        delivery: newMarker.delivery ? new Date(newMarker.delivery).toISOString() : null,
        pickup: newMarker.pickup ? new Date(newMarker.pickup).toISOString() : null,
        operationLocationDelivery: newMarker.operationLocationDelivery,
  operationLocationPickup: newMarker.operationLocationPickup,
  transportNotes: newMarker.transportNotes,
      };

      const response = await api.post('/transportmarkers', payload);
      console.log('Neuer TransportMarker erstellt:', response.data);
      alert('TransportMarker erfolgreich erstellt!');
      setShowForm(false);
      fetchTransportMarkers();
    } catch (error) {
      console.error('Fehler beim Erstellen des TransportMarkers:', error);
      if (error.response && error.response.data && error.response.data.message) {
        alert(`Fehler: ${error.response.data.message}`);
      } else {
        alert('Fehler beim Erstellen des TransportMarkers.');
      }
    }
  };


  // Handler zum Update aller TransportMarkers
  const handleBulkUpdateMarkers = async () => {
    // Bestätigung vom Benutzer einholen
    if (!window.confirm('Möchten Sie alle TransportMarkers dieses Auftrags aktualisieren?')) {
      return;
    }
  
    try {
      const response = await api.put(`/transportmarkers/byOrder/${orderId}/updateAll`);
      console.log('TransportMarkers aktualisiert:', response.data);
      alert(response.data.message);
      fetchTransportMarkers(); // Aktualisiere die Liste
    } catch (error) {
      console.error('Fehler beim Aktualisieren der TransportMarkers:', error);
      if (error.response && error.response.data && error.response.data.message) {
        alert(`Fehler: ${error.response.data.message}`);
      } else {
        alert('Fehler beim Aktualisieren der TransportMarkers.');
      }
    }
  };

  // Handler zum Löschen eines TransportMarkers
  const handleDeleteMarker = async (markerId) => {
    if (!window.confirm(`Möchtest du den TransportMarker #${markerId} wirklich löschen?`)) return;
  
    try {
      await api.delete(`/transportmarkers/${markerId}`);
      alert('TransportMarker erfolgreich gelöscht!');
      // Aktualisiere den lokalen State, indem du den gelöschten Marker herausfilterst:
      setTransportMarkers((prevMarkers) => prevMarkers.filter(marker => marker.id !== markerId));
    } catch (error) {
      console.error('Fehler beim Löschen des TransportMarkers:', error);
      alert('Fehler beim Löschen des TransportMarkers.');
    }
  };
  

  // Handler zum Abbrechen der Bearbeitung
  const handleCancelEdit = () => {
    setShowForm(false);
    setEditMarkerId(null);
  };

  // Handler zur automatischen Erstellung der TransportMarkers
  const handleAutoCreateMarkers = async () => {
    if (autoCreated) {
      alert("TransportMarkers wurden bereits automatisch erstellt.");
      return;
    }

    if (!order) {
      alert("Auftragsdaten sind nicht geladen.");
      return;
    }

    const rentalOrderItems = orderItems.filter(item => item.item && item.item.itemType === 'rental');

    if (rentalOrderItems.length === 0) {
      alert("Keine OrderItems mit dem Typ 'rental' gefunden.");
      return;
    }

    // Bestätigung vom Benutzer einholen
    if (!window.confirm(`Möchtest du TransportMarkers für ${rentalOrderItems.length} OrderItems erstellen?`)) {
      return;
    }

    setIsAutoCreating(true);

    try {
      const payload = {
        orderId: parseInt(orderId, 10),
      };

      const response = await api.post('/transportmarkers/bulk', payload);

      console.log("TransportMarkers erstellt:", response.data);
      alert(response.data.message);

      fetchTransportMarkers(); // Aktualisiere die Liste

      setAutoCreated(true);
    } catch (error) {
      console.error("Fehler beim automatischen Erstellen der TransportMarkers:", error);
      if (error.response && error.response.data && error.response.data.message) {
        alert(`Fehler: ${error.response.data.message}`);
      } else {
        alert("Fehler beim Erstellen der TransportMarkers.");
      }
    } finally {
      setIsAutoCreating(false);
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
       <Helmet>
        <title>Transportmarker bearbeiten</title>
        <meta name="description" content="Transportmarker bearbeiten" />
      </Helmet>
      <h2>TransportMarker für Auftrag #{orderId}, {order?.orderNumber || 'Keine Auftragsnummer vorhanden'}</h2>

      {/* Buttons */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {/* Button zum Zurückkehren zur Auftragsansicht */}
        <button
          type="button"
          onClick={() => navigate(`/orders/${orderId}/edit`)} // Korrigierte Route
          style={{ marginBottom: '20px', padding: '5px 10px', cursor: 'pointer' }}
        >
          Zurück zum Auftrag
        </button>

        {/* Button zum Erstellen eines neuen TransportMarkers */}
        <button
          type="button"
          onClick={() => {
            setShowForm(true);
            setEditMarkerId(null); // Stellen Sie sicher, dass kein Marker bearbeitet wird
            setNewMarker({
              orderItemId: '',
              delivery: '',
              pickup: '',
              operationLocationDelivery: order ? order.operationLocation : '',
              operationLocationPickup: order ? order.operationLocation : '',
              transportNotes: order ? order.notes : '',
            });
          }}
          style={{
            marginBottom: '20px',
            padding: '5px 10px',
            backgroundColor: '#28a745',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginLeft: '10px',
          }}
        >
          Neuen TransportMarker erstellen
        </button>

        {/* Neuer Button zur automatischen Erstellung der TransportMarkers */}
        <button
          type="button"
          onClick={handleAutoCreateMarkers}
          disabled={autoCreated || isAutoCreating || !order} // Button deaktivieren, wenn autoCreated, isAutoCreating oder order nicht geladen
          style={{
            marginBottom: '20px',
            padding: '5px 10px',
            backgroundColor: '#17a2b8',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: autoCreated || isAutoCreating || !order ? 'not-allowed' : 'pointer',
            marginLeft: '10px',
          }}
        >
          {isAutoCreating ? 'Erstelle TransportMarkers...' : 'TransportMarkers automatisch erstellen'}
        </button>

        {/* Neuer Button zur Bulk-Aktualisierung der TransportMarkers */}
<button
  type="button"
  onClick={handleBulkUpdateMarkers}
  style={{
    marginBottom: '20px',
    padding: '5px 10px',
    backgroundColor: '#ffc107',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    marginLeft: '10px',
  }}
>
  Alle TransportMarkers aktualisieren
</button>

      </div>

      {/* TransportMarker-Liste */}
      {isLoading ? (
        <p>Lade TransportMarker...</p>
      ) : transportMarkers.length === 0 ? (
        <p>Keine TransportMarkers für diesen Auftrag vorhanden.</p>
      ) : (
        <table border="1" cellPadding="6" style={{ marginTop: '1rem', width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Order Number</th>
              <th>Customer Company</th>
              <th>OrderItem Name</th>
              <th>LW Operator</th>
              <th>Delivery</th>
              <th>Pickup</th>
              <th>Op. Location Delivery</th>
              <th>Op. Location Pickup</th>
              <th>Transport Notes</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {transportMarkers.map((marker) => {
              // Da wir die Order-Daten separat geladen haben, verwenden wir den 'orderId' zur Zuordnung
              const associatedOrderItem = orderItems.find(item => item.id === marker.orderItemId);
              const orderNumber = order ? order.orderNumber : '–';
              const customerCompany = order && order.customer ? order.customer.company : '–';
              const lwOperator = order && order.lwOperator ? `${order.lwOperator.contactFirstName} ${order.lwOperator.contactLastName}` : '–';
              const itemName = associatedOrderItem && associatedOrderItem.item ? associatedOrderItem.item.name : '–';

              return (
                <tr key={marker.id}>
                  <td>{marker.id}</td>
                  <td>{orderNumber}</td>
                  <td>{customerCompany}</td>
                  <td>{itemName}</td>
                  <td>{lwOperator}</td>
                  <td>
                    {marker.delivery
                      ? new Date(marker.delivery).toLocaleDateString('de-DE', {
                          timeZone: 'Europe/Berlin',
                          day: 'numeric',
                          month: 'numeric',
                          year: 'numeric',
                        })
                      : '–'}
                  </td>
                  <td>
                    {marker.pickup
                      ? new Date(marker.pickup).toLocaleDateString('de-DE', {
                          timeZone: 'Europe/Berlin',
                          day: 'numeric',
                          month: 'numeric',
                          year: 'numeric',
                        })
                      : '–'}
                  </td>
                  <td>{marker.operationLocationDelivery || '–'}</td>
                  <td>{marker.operationLocationPickup || '–'}</td>
                  <td>{marker.transportNotes || '–'}</td>
                  <td>
                    {/* Bearbeiten-Button */}
                    <button
                      type="button"
                      onClick={() => handleEditMarker(marker.id)}
                      style={{
                        padding: '3px 6px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        marginRight: '5px',
                      }}
                    >
                      Bearbeiten
                    </button>
                    {/* Löschen-Button */}
                    <button
                      type="button"
                      onClick={() => handleDeleteMarker(marker.id)}
                      style={{
                        padding: '3px 6px',
                        backgroundColor: '#dc3545',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                      }}
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Formular zum Erstellen oder Bearbeiten eines TransportMarkers */}
      {showForm && (
        <div style={{ marginTop: '20px', border: '1px solid #ccc', padding: '15px', borderRadius: '5px' }}>
          <h3>{editMarkerId ? `TransportMarker #${editMarkerId} bearbeiten` : 'Neuen TransportMarker erstellen'}</h3>
          {orderItems.filter(orderItem => orderItem.item && orderItem.item.itemType === 'rental').length === 0 ? (
            <p>Keine OrderItems mit dem Typ "rental" verfügbar. Bitte erstelle zuerst ein entsprechendes OrderItem.</p>
          ) : (
            <form onSubmit={editMarkerId ? handleEditFormSubmit : handleCreateMarker}>
              {/* OrderItem Auswahl */}
              <label htmlFor="orderItemId" style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '400px',marginLeft: '465px'  }}>
                OrderItem:
                <select
                  id="orderItemId"
                  name="orderItemId"
                  value={editMarkerId ? editFormData.orderItemId : newMarker.orderItemId}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  required
                  style={{ marginLeft: '10px', padding: '10px', width: '100%' }}
                >
                  <option value="">-- Bitte wählen --</option>
                  {orderItems
                    .filter(orderItem => orderItem.item && orderItem.item.itemType === 'rental')
                    .map(orderItem => (
                      <option key={orderItem.id} value={orderItem.id}>
                        {orderItem.item.name} (#{orderItem.id})
                      </option>
                  ))}
                </select>
              </label>

              {/* Delivery Datum */}
              <label htmlFor="delivery" style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '200px',marginLeft: '480px'  }}>
                Delivery:
                <input
                  type="date"
                  id="delivery"
                  name="delivery"
                  value={editMarkerId ? editFormData.delivery : newMarker.delivery}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  style={{ marginLeft: '10px', padding: '10px', width: '100%' }}
                />
              </label>

              {/* Pickup Datum */}
              <label htmlFor="pickup" style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '200px',marginLeft: '490px'  }}>
                Pickup:
                <input
                  type="date"
                  id="pickup"
                  name="pickup"
                  value={editMarkerId ? editFormData.pickup : newMarker.pickup}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  style={{ marginLeft: '10px', padding: '10px', width: '100%' }}
                />
              </label>
              {/* Operation Location Delivery */}
              <label
                htmlFor="operationLocationDelivery"
                style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '400px', marginLeft: '458px', marginTop: '10px' }}
              >
                Ort Anlieferung:
                <input
                  type="text"
                  id="operationLocationDelivery"
                  name="operationLocationDelivery"
                  value={editMarkerId ? editFormData.operationLocationDelivery : newMarker.operationLocationDelivery}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  style={{ marginLeft: '10px', padding: '10px', width: '100%' }}
                />
              </label>

              {/* Operation Location Pickup */}
              <label
                htmlFor="operationLocationPickup"
                style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '400px', marginLeft: '467px', marginTop: '10px' }}
              >
                Ort Abholung:
                <input
                  type="text"
                  id="operationLocationPickup"
                  name="operationLocationPickup"
                  value={editMarkerId ? editFormData.operationLocationPickup : newMarker.operationLocationPickup}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  style={{ marginLeft: '10px', padding: '10px', width: '100%' }}
                />
              </label>

              {/* Transport Notes */}
              <label
                htmlFor="transportNotes"
                style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '400px', marginLeft: '460px', marginTop: '10px' }}
              >
                Transport Notes:
                <textarea
                  id="transportNotes"
                  name="transportNotes"
                  value={editMarkerId ? editFormData.transportNotes : newMarker.transportNotes}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  style={{ marginLeft: '10px', padding: '10px', width: '100%', height: '80px' }}
                />
              </label>

              {/* Formular-Buttons */}
              <div style={{ marginTop: '15px' }}>
                <button
                  type="submit"
                  style={{
                    padding: '5px 10px',
                    backgroundColor: editMarkerId ? '#007bff' : '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    marginRight: '10px',
                  }}
                >
                  {editMarkerId ? 'Aktualisieren' : 'Erstellen'}
                </button>
                {editMarkerId ? (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#6c757d',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                    }}
                  >
                    Abbrechen
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#6c757d',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                    }}
                  >
                    Abbrechen
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default TransportMarkerListOrder;
